import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import Select from "react-select";
import { ChevronUpIcon, ChevronDownIcon, XIcon } from "@heroicons/react/outline";
import { OverlayTrigger, Button } from "react-bootstrap";
import { updateFilter } from '../../actions/filters';
import { SearchClient } from "../../lib/SearchClient";
import './AdvancedFilter.scss';

const AdvancedFilter = props => {
  const settings = useSelector(({ settings }) => settings);
  const filters = useSelector(({ filters }) => filters);
  const defaultFilters = useSelector(({ settings }) => _.get(settings, 'userInfo.searchOptions', {}));

  const [isOpenAdvancedFilter, setIsOpenAdvancedFilter] = React.useState(false);
  const [funnelStageValue, setFunnelStageValue] = React.useState(null);
  const [contentTypeValue, setContentTypeValue] = React.useState(null);
  const [tagValue, setTagValue] = React.useState(null);
  const [productValue, setProductValue] = React.useState(null);
  const [industryValue, setIndustryValue] = React.useState(null);
  const [regionValue, setRegionValue] = React.useState(null);

  React.useEffect(() => {
    if (isOpenAdvancedFilter) {
      if (_.get(filters, 'funnelStages') !== undefined) {
        setFunnelStageValue(_.get(filters, 'funnelStages', []).map(val => ({ label: val, value: val })));
        setContentTypeValue(_.get(filters, 'contentTypes', []).map(val => ({ label: val, value: val })));
        setTagValue(_.get(filters, 'contentTags', []).map(val => ({ label: val, value: val })));
        setProductValue(_.get(filters, 'contentProducts', []).map(val => ({ label: val, value: val })));
        setIndustryValue(_.get(filters, 'contentIndustries', []).map(val => ({ label: val, value: val })));
        setRegionValue(_.get(filters, 'contentRegions', []).map(val => ({ label: val, value: val })));
      } else {
        setFunnelStageValue(_.get(filters, 'funnelStages', []).map(val => ({ label: val, value: val })));
        setContentTypeValue(_.get(filters, 'contentTypes', []).map(val => ({ label: val, value: val })));
        setTagValue(_.get(defaultFilters, 'tags', []).map(val => ({ label: val, value: val })));
        setProductValue(_.get(defaultFilters, 'products', []).map(val => ({ label: val, value: val })));
        setIndustryValue(_.get(defaultFilters, 'industries', []).map(val => ({ label: val, value: val })));
        setRegionValue(_.get(defaultFilters, 'regions', []).map(val => ({ label: val, value: val })));
      }
    }
  }, [isOpenAdvancedFilter])

  const dispatch = useDispatch();

  const onEnterOverlay = (e) => {
    setIsOpenAdvancedFilter(true);
  };

  const onExitOverlay = (e) => {
    setIsOpenAdvancedFilter(false);
  };

  const generateOptions = optionValues => {
    if (optionValues) {
      const keys = Object.keys(optionValues);
      return keys.map(key => ({ value: key, label: key }));
    }
    
    return [];
  }

  const resetFilter = () => {
    setFunnelStageValue(null);
    setContentTypeValue(null);

    setTagValue(_.get(defaultFilters, 'tags', []).map(val => ({ label: val, value: val })));
    setProductValue(_.get(defaultFilters, 'products', []).map(val => ({ label: val, value: val })));
    setIndustryValue(_.get(defaultFilters, 'industries', []).map(val => ({ label: val, value: val })));
    setRegionValue(_.get(defaultFilters, 'regions', []).map(val => ({ label: val, value: val })));

    const filterValues = {
      funnelStages: [],
      contentTypes: [],
      contentTags: _.get(defaultFilters, 'tags', []),
      contentRegions: _.get(defaultFilters, 'regions', []),
      contentProducts: _.get(defaultFilters, 'products', []),
      contentIndustries: _.get(defaultFilters, 'industries', []),
      searchKey: ''
    };

    const searchOptions = {
      funnelState: [],
      contentType: [],
      tags: _.get(defaultFilters, 'tags', []),
      regions: _.get(defaultFilters, 'regions', []),
      products: _.get(defaultFilters, 'products', []),
      industries: _.get(defaultFilters, 'industries', [])
    };

    const searchClient = SearchClient.getClient();
    if (searchClient) {
      searchClient.updateSearchOptions(searchOptions);
    }

    if (props.onChange) {
      props.onChange(true, filterValues);
    }
  }

  const handleSubmitFilter = () => {
    const funnelOptions = (funnelStageValue || []).map(val => val.label);
    const typeOptions = (contentTypeValue || []).map(val => val.label);
    const tagOptions = (tagValue || []).map(val => val.label);
    const regionOptions = (regionValue || []).map(val => val.label);
    const productOptions = (productValue || []).map(val => val.label);
    const industryOptions = (industryValue || []).map(val => val.label);

    const filterValues = {
      funnelStages: funnelOptions,
      contentTypes: typeOptions,
      contentTags: tagOptions,
      contentRegions: regionOptions,
      contentProducts: productOptions,
      contentIndustries: industryOptions
    };

    const searchOptions = {
      funnelStage: funnelOptions,
      contentType: typeOptions,
      tags: tagOptions,
      regions: regionOptions,
      products: productOptions,
      industries: industryOptions
    };

    const searchClient = SearchClient.getClient();
    if (searchClient) {
      searchClient.updateSearchOptions(searchOptions);
    }

    // dispatch(updateFilter(filterValues));
    setIsOpenAdvancedFilter(false);

    if (props.onChange) {
      props.onChange(false, filterValues);
    }
  }

  const handleSelectChange = (type, value) => {
    switch (type) {
      case 'funnel':
        setFunnelStageValue(value);
        break;
      case 'type':
        setContentTypeValue(value);
        break;
      case 'tag':
        setTagValue(value);
        break;
      case 'product':
        setProductValue(value);
        break;
      case 'industry':
        setIndustryValue(value);
        break;
      case 'region':
        setRegionValue(value);
        break;
      default:
        break;
    }
  }

  const calcFiltersCount = () => {
    const funnelFilterCount = _.get(filters, 'funnelStages', []).length;
    const typeFilterCount = _.get(filters, 'contentTypes', []).length;
    const tagFilterCount = _.get(filters, 'contentTags', []).length;
    const productFilterCount = _.get(filters, 'contentProducts', []).length;
    const industryFilterCount = _.get(filters, 'contentIndustries', []).length;
    const regionFilterCount = _.get(filters, 'contentRegions', []).length;

    return funnelFilterCount + typeFilterCount + tagFilterCount + productFilterCount + industryFilterCount + regionFilterCount;
  };
          
  const filterCounts = calcFiltersCount();
  return (
    <div className="cursor-pointer advanced-filter-wrapper">
      <OverlayTrigger
        trigger="click"
        rootClose={true}
        placement="bottom"
        className="advanced-filter-popup"
        onClick={e => e.preventDefault()}
        onEnter={onEnterOverlay}
        onExit={onExitOverlay}
        show={isOpenAdvancedFilter}
        overlay={
          <div className="advanced-filter-container">
            <div className="title flex items-center justify-between">
              Advanced Filter
              <XIcon className="cursor-pointer icons" onClick={e => setIsOpenAdvancedFilter(false)} />
            </div>
            <div className="options-box">
              <div className="select-filter-row flex justify-between">
                <div className="filter-name">Funnel stages</div>
                <Select
                  className="w-100 select-box funnel-stage"
                  classNamePrefix="filter_"
                  closeOnSelect={true}
                  isMulti
                  onChange={value => handleSelectChange('funnel', value)}
                  options={generateOptions(_.get(settings, 'funnelStages', {}))}
                  placeholder="Select funnel stages"
                  removeSelected={true}
                  value={funnelStageValue}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSubmitFilter();
                    }
                  }}
                />
              </div>

              <div className="select-filter-row flex justify-between">
                <div className="filter-name">Content types</div>
                <Select
                  classNamePrefix="filter_"
                  className="w-100 select-box content-type"
                  closeOnSelect={true}
                  isMulti
                  onChange={value => handleSelectChange('type', value)}
                  options={generateOptions(_.get(settings, 'contentTypes', {}))}
                  placeholder="Select content types"
                  removeSelected={true}
                  value={contentTypeValue}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSubmitFilter();
                    }
                  }}
                />
              </div>

              <div className="separator" />

              <div className="select-filter-row flex justify-between">
                <div className="filter-name">Tags</div>
                <Select
                  classNamePrefix="filter_"
                  className="w-100 select-box tag"
                  closeOnSelect={true}
                  isMulti
                  onChange={value => handleSelectChange('tag', value)}
                  options={generateOptions(_.get(settings, 'contentTags', {}))}
                  placeholder="Select tags"
                  removeSelected={true}
                  value={tagValue}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSubmitFilter();
                    }
                  }}
                />
              </div>

              {_.get(settings, 'appOptions.enableProducts', false) && 
              <div className="select-filter-row flex justify-between">
                <div className="filter-name">Products</div>
                <Select
                  classNamePrefix="filter_"
                  className="w-100 select-box product"
                  closeOnSelect={true}
                  isMulti
                  onChange={value => handleSelectChange('product', value)}
                  options={generateOptions(_.get(settings, 'contentProducts', {}))}
                  placeholder="Select products"
                  removeSelected={true}
                  value={productValue}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSubmitFilter();
                    }
                  }}
                />
              </div>}

              {_.get(settings, 'appOptions.enableIndustries', false) &&
              <div className="select-filter-row flex justify-between">
                <div className="filter-name">Industries</div>
                <Select
                  classNamePrefix="filter_"
                  className="w-100 select-box industry"
                  closeOnSelect={true}
                  isMulti
                  onChange={value => handleSelectChange('industry', value)}
                  options={generateOptions(_.get(settings, 'contentIndustries', {}))}
                  placeholder="Select industries"
                  removeSelected={true}
                  value={industryValue}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSubmitFilter();
                    }
                  }}
                />
              </div>}

              {_.get(settings, 'appOptions.enableRegions', false) &&
              <div className="select-filter-row flex justify-between">
                <div className="filter-name">Regions</div>
                <Select
                  classNamePrefix="filter_"
                  className="w-100 select-box region"
                  closeOnSelect={true}
                  isMulti
                  onChange={value => handleSelectChange('region', value)}
                  options={generateOptions(_.get(settings, 'contentRegions', {}))}
                  placeholder="Select regions"
                  removeSelected={true}
                  value={regionValue}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSubmitFilter();
                    }
                  }}
                />
              </div>}
            </div>
            <div className="action-box flex items-center justify-between">
              <div>
                <Link to="/settings">Set default search filters...</Link>
              </div>
              <div>
                <Button
                  tabIndex="0"
                  variant="secondary"
                  className="button button__reset mr-2"
                  onClick={resetFilter}
                >
                  RESET
                </Button>
                <Button
                  tabIndex="1"
                  variant="cta"
                  className="button button__find"
                  onClick={handleSubmitFilter}
                  onKeyPress={(event) => {
                    if (event.key === "Enter") {
                      handleSubmitFilter();
                    }
                  }}
                >
                  FIND
                </Button>
              </div>
            </div>
          </div>
        }
      >
        {
          filterCounts > 0 ?
            <div className="filter-count-indicator" onClick={e => setIsOpenAdvancedFilter(!isOpenAdvancedFilter)}>{filterCounts}</div>
            : (
              isOpenAdvancedFilter ? 
                <ChevronUpIcon className="icons" onClick={e => setIsOpenAdvancedFilter(!isOpenAdvancedFilter)} /> : 
                <ChevronDownIcon className="icons" onClick={e => setIsOpenAdvancedFilter(!isOpenAdvancedFilter)} />)
        }
      </OverlayTrigger>
    </div>
  )
};

export default AdvancedFilter;
